import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  addWorkspaceMemberResultSchema,
  workspaceMemberListSchema,
  workspaceMemberSchema,
  workspaceSchema,
  workspaceBoardVisibilityPolicyListSchema,
  workspaceBoardVisibilityPolicySchema,
  workspaceGuestInviteRuleSettingSchema,
  workspaceGuestListSchema,
  type AddWorkspaceMemberResult,
  type ApiError,
  type BoardVisibility,
  type Workspace,
  type WorkspaceBoardVisibilityPolicy,
  type WorkspaceBoardVisibilityPolicyPatch,
  type WorkspaceGuest,
  type WorkspaceGuestInviteRule,
  type WorkspaceGuestInviteRuleSetting,
  type WorkspaceMember,
  type WorkspaceProfilePatch,
  type WorkspaceRole,
  type WorkspaceVisibility,
} from '@molt/shared';
import { del, emptyResponseSchema, get, patch, post, request } from './api';
import { queryKeys } from './query-keys';

/**
 * The workspace endpoints and their hooks — chunk `D2`, agent A
 * (`FE-07.1`, `FE-07.2`, `FE-07.3`).
 *
 * `lib/profile.ts` is the house pattern this file follows: endpoint functions
 * spelling their own path on top of `./api`'s verbs, then hooks whose
 * `onSuccess` writes the cache and never announces. `D2-BRIEF.md` §6.1 is the
 * contract; the two things worth restating here rather than at each hook:
 *
 *  - **No optimistic write.** Every mutation in this chunk sits behind a button
 *    already showing `busy`, so there is nothing an optimistic write would make
 *    feel faster. `lib/optimistic.ts` exists and this module is deliberately
 *    not a caller of it.
 *  - **The hook's `onSuccess` owns the cache write; the caller's owns the
 *    announcement.** A caller that declares its own `onSuccess` on the returned
 *    mutation does not replace this one — TanStack Query calls both — but
 *    putting the cache write here is what stops three call sites drifting into
 *    three different ones.
 *
 * Paths are the **server's** spelling: `./api` prepends `BASE = '/api'` inside
 * `request()`, so `/workspaces` here is `/api/workspaces` on the wire.
 */

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** `GET /workspaces` — every workspace the caller is a member of. 401 with no session. */
export function getWorkspaces(): Promise<Workspace[]> {
  return get('/workspaces', workspaceSchema.array());
}

/**
 * `GET /workspaces/:id/members`.
 *
 * ⚠ **404 for a non-member and for a soft-deleted workspace, identically** —
 * `requireWorkspaceMember` folds the two so the route is not an existence
 * oracle. The surface must not try to tell them apart.
 */
export function getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/members`,
    workspaceMemberListSchema,
  );
}

/** `POST /workspaces` — 201. The creator becomes its admin; `shortName` is derived server-side. */
export function createWorkspace(name: string): Promise<Workspace> {
  return post('/workspaces', workspaceSchema, { name });
}

/**
 * `POST /workspaces/:id/members` — admin only, **200 for both outcomes**.
 *
 * ⚠ `result.added` is the distinction `FR-PERM-021` AC4 turns on: `false` is
 * the no-op re-add, which the surface must state as an existing membership
 * rather than report as a success or a failure. The HTTP status carries
 * nothing.
 */
export function addWorkspaceMember(
  workspaceId: string,
  handle: { email: string } | { username: string },
): Promise<AddWorkspaceMemberResult> {
  return post(
    `/workspaces/${encodeURIComponent(workspaceId)}/members`,
    addWorkspaceMemberResultSchema,
    handle,
  );
}

/**
 * `PATCH /workspaces/:id/members/:userId/role` — admin only.
 *
 * ⚠ **409 when the demotion would leave the workspace with no admin.** The
 * server takes a row lock and counts; this client does not pre-empt it
 * (`D2-BRIEF.md` §5.5) — it surfaces the message.
 */
export function updateWorkspaceMemberRole(
  workspaceId: string,
  userId: string,
  role: WorkspaceRole,
): Promise<WorkspaceMember> {
  return patch(
    `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}/role`,
    workspaceMemberSchema,
    { role },
  );
}

/** `GET /workspaces/:id` — the workspace settings host. */
export function getWorkspace(workspaceId: string): Promise<Workspace> {
  return get(`/workspaces/${encodeURIComponent(workspaceId)}`, workspaceSchema);
}

/** `PATCH /workspaces/:id/profile` — admin-only profile fields. */
export function updateWorkspaceProfile(
  workspaceId: string,
  patchBody: WorkspaceProfilePatch,
): Promise<Workspace> {
  return patch(
    `/workspaces/${encodeURIComponent(workspaceId)}/profile`,
    workspaceSchema,
    patchBody,
  );
}

/** `PATCH /workspaces/:id/visibility` — workspace profile visibility, not board visibility. */
export function updateWorkspaceVisibility(
  workspaceId: string,
  visibility: WorkspaceVisibility,
): Promise<Workspace> {
  return patch(
    `/workspaces/${encodeURIComponent(workspaceId)}/visibility`,
    workspaceSchema,
    { visibility },
  );
}

/** `GET /workspaces/:id/board-visibility-policies` — readable by workspace members. */
export function getWorkspaceBoardVisibilityPolicies(
  workspaceId: string,
): Promise<WorkspaceBoardVisibilityPolicy[]> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/board-visibility-policies`,
    workspaceBoardVisibilityPolicyListSchema,
  );
}

/** `PATCH /workspaces/:id/board-visibility-policies/:visibility` — admin-only, field-scoped. */
export function updateWorkspaceBoardVisibilityPolicy(
  workspaceId: string,
  visibility: BoardVisibility,
  body: WorkspaceBoardVisibilityPolicyPatch,
): Promise<WorkspaceBoardVisibilityPolicy> {
  return patch(
    `/workspaces/${encodeURIComponent(workspaceId)}/board-visibility-policies/${encodeURIComponent(visibility)}`,
    workspaceBoardVisibilityPolicySchema,
    body,
  );
}

/** `GET /workspaces/:id/guest-invite-rule` — admin-only. */
export function getWorkspaceGuestInviteRule(
  workspaceId: string,
): Promise<WorkspaceGuestInviteRuleSetting> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/guest-invite-rule`,
    workspaceGuestInviteRuleSettingSchema,
  );
}

/** `PATCH /workspaces/:id/guest-invite-rule` — admin-only and field-scoped. */
export function updateWorkspaceGuestInviteRule(
  workspaceId: string,
  guestInviteRule: WorkspaceGuestInviteRule,
): Promise<WorkspaceGuestInviteRuleSetting> {
  return patch(
    `/workspaces/${encodeURIComponent(workspaceId)}/guest-invite-rule`,
    workspaceGuestInviteRuleSettingSchema,
    { guestInviteRule },
  );
}

/** `GET /workspaces/:id/guests` — derived board guests, admin-only. */
export function getWorkspaceGuests(workspaceId: string): Promise<WorkspaceGuest[]> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/guests`,
    workspaceGuestListSchema,
  );
}

/** `DELETE /workspaces/:id/guests/:userId` — removes that user's board memberships in the workspace. */
export function removeWorkspaceGuest(workspaceId: string, userId: string): Promise<null> {
  return del(
    `/workspaces/${encodeURIComponent(workspaceId)}/guests/${encodeURIComponent(userId)}`,
    emptyResponseSchema,
  );
}

/**
 * `DELETE /workspaces/:id` — 204 with a JSON body.
 *
 * `del()` is deliberately bodyless, so this route goes through `request()`.
 */
export function deleteWorkspace(workspaceId: string, confirmName: string): Promise<null> {
  return request(`/workspaces/${encodeURIComponent(workspaceId)}`, emptyResponseSchema, {
    method: 'DELETE',
    body: JSON.stringify({ confirmName }),
  });
}

/**
 * `DELETE /workspaces/:id/members/:userId` — 204 with the removal scope in the body.
 */
export function removeWorkspaceMember(
  workspaceId: string,
  userId: string,
  removeFromBoards: boolean,
): Promise<null> {
  return request(
    `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}`,
    emptyResponseSchema,
    {
      method: 'DELETE',
      body: JSON.stringify({ removeFromBoards }),
    },
  );
}

/** `DELETE /workspaces/:id/members/me` — bodyless workspace leave. */
export function leaveWorkspace(workspaceId: string): Promise<null> {
  return request(
    `/workspaces/${encodeURIComponent(workspaceId)}/members/me`,
    emptyResponseSchema,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Query options
// ---------------------------------------------------------------------------

export function workspacesQueryOptions(): UseQueryOptions<Workspace[], ApiError> {
  return {
    queryKey: queryKeys.workspaces.all(),
    queryFn: getWorkspaces,
  };
}

export function workspaceMembersQueryOptions(
  workspaceId: string,
): UseQueryOptions<WorkspaceMember[], ApiError> {
  return {
    queryKey: queryKeys.workspaces.members(workspaceId),
    queryFn: () => getWorkspaceMembers(workspaceId),
  };
}

export function workspaceQueryOptions(
  workspaceId: string,
): UseQueryOptions<Workspace, ApiError> {
  return {
    queryKey: queryKeys.workspaces.detail(workspaceId),
    queryFn: () => getWorkspace(workspaceId),
  };
}

export function workspaceBoardVisibilityPoliciesQueryOptions(
  workspaceId: string,
): UseQueryOptions<WorkspaceBoardVisibilityPolicy[], ApiError> {
  return {
    queryKey: queryKeys.workspaces.boardVisibilityPolicies(workspaceId),
    queryFn: () => getWorkspaceBoardVisibilityPolicies(workspaceId),
  };
}

export function workspaceGuestInviteRuleQueryOptions(
  workspaceId: string,
): UseQueryOptions<WorkspaceGuestInviteRuleSetting, ApiError> {
  return {
    queryKey: queryKeys.workspaces.guestInviteRule(workspaceId),
    queryFn: () => getWorkspaceGuestInviteRule(workspaceId),
  };
}

export function workspaceGuestsQueryOptions(
  workspaceId: string,
): UseQueryOptions<WorkspaceGuest[], ApiError> {
  return {
    queryKey: queryKeys.workspaces.guests(workspaceId),
    queryFn: () => getWorkspaceGuests(workspaceId),
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useWorkspaces(): UseQueryResult<Workspace[], ApiError> {
  return useQuery(workspacesQueryOptions());
}

export function useWorkspaceMembers(
  workspaceId: string,
): UseQueryResult<WorkspaceMember[], ApiError> {
  return useQuery(workspaceMembersQueryOptions(workspaceId));
}

export function useWorkspace(workspaceId: string): UseQueryResult<Workspace, ApiError> {
  return useQuery(workspaceQueryOptions(workspaceId));
}

export function useWorkspaceBoardVisibilityPolicies(
  workspaceId: string,
  enabled = true,
): UseQueryResult<WorkspaceBoardVisibilityPolicy[], ApiError> {
  return useQuery({
    ...workspaceBoardVisibilityPoliciesQueryOptions(workspaceId),
    enabled,
  });
}

export function useWorkspaceGuestInviteRule(
  workspaceId: string,
  enabled: boolean,
): UseQueryResult<WorkspaceGuestInviteRuleSetting, ApiError> {
  return useQuery({ ...workspaceGuestInviteRuleQueryOptions(workspaceId), enabled });
}

export function useWorkspaceGuests(
  workspaceId: string,
  enabled: boolean,
): UseQueryResult<WorkspaceGuest[], ApiError> {
  return useQuery({ ...workspaceGuestsQueryOptions(workspaceId), enabled });
}

/**
 * `POST /workspaces`. §5.2 / §6.1 rule 2: **appends**, never prepends or
 * sorts — the landing page's group order depends on the server's
 * `created_at, id` order surviving into the cache, and a freshly created
 * workspace is always the newest one.
 */
export function useCreateWorkspace(): UseMutationResult<Workspace, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: (created) => {
      queryClient.setQueryData(
        queryKeys.workspaces.all(),
        (prev: Workspace[] | undefined) => [...(prev ?? []), created],
      );
    },
  });
}

/**
 * `POST /workspaces/:id/members`. §6.1 rule 2: the response shape
 * (`AddWorkspaceMemberResult`) is not the member list's row shape, so this
 * invalidates the members key rather than writing into it directly — the
 * one write in this file that is not a `setQueryData`.
 */
export function useAddWorkspaceMember(
  workspaceId: string,
): UseMutationResult<
  AddWorkspaceMemberResult,
  ApiError,
  { email: string } | { username: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (handle: { email: string } | { username: string }) =>
      addWorkspaceMember(workspaceId, handle),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.members(workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.joinRequests(workspaceId),
      });
    },
  });
}

/**
 * `PATCH /workspaces/:id/members/:userId/role`. §6.1 rule 2: replaces the
 * one row in `queryKeys.workspaces.members(workspaceId)` by `id`, rather
 * than invalidating — the response is the complete updated row.
 */
export function useUpdateWorkspaceMemberRole(
  workspaceId: string,
): UseMutationResult<WorkspaceMember, ApiError, { userId: string; role: WorkspaceRole }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: WorkspaceRole }) =>
      updateWorkspaceMemberRole(workspaceId, userId, role),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        queryKeys.workspaces.members(workspaceId),
        (prev: WorkspaceMember[] | undefined) =>
          prev?.map((member) => (member.id === updated.id ? updated : member)),
      );
    },
  });
}

function writeWorkspace(
  queryClient: ReturnType<typeof useQueryClient>,
  workspace: Workspace,
) {
  queryClient.setQueryData(queryKeys.workspaces.detail(workspace.id), workspace);
  queryClient.setQueryData(
    queryKeys.workspaces.all(),
    (previous: Workspace[] | undefined) =>
      previous?.map((item) => (item.id === workspace.id ? workspace : item)),
  );
}

function removeWorkspaceFromCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
) {
  queryClient.removeQueries({ queryKey: queryKeys.workspaces.detail(workspaceId) });
  queryClient.removeQueries({ queryKey: queryKeys.workspaces.members(workspaceId) });
  queryClient.setQueryData(
    queryKeys.workspaces.all(),
    (previous: Workspace[] | undefined) =>
      previous?.filter((workspace) => workspace.id !== workspaceId),
  );
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.pages() });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.workspaces.boards(workspaceId),
  });
}

export function useUpdateWorkspaceProfile(
  workspaceId: string,
): UseMutationResult<Workspace, ApiError, WorkspaceProfilePatch> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patchBody: WorkspaceProfilePatch) =>
      updateWorkspaceProfile(workspaceId, patchBody),
    onSuccess: (workspace) => {
      writeWorkspace(queryClient, workspace);
    },
  });
}

export function useUpdateWorkspaceVisibility(
  workspaceId: string,
): UseMutationResult<Workspace, ApiError, WorkspaceVisibility> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (visibility: WorkspaceVisibility) =>
      updateWorkspaceVisibility(workspaceId, visibility),
    onSuccess: (workspace) => {
      writeWorkspace(queryClient, workspace);
    },
  });
}

export function useUpdateWorkspaceBoardVisibilityPolicy(
  workspaceId: string,
): UseMutationResult<
  WorkspaceBoardVisibilityPolicy,
  ApiError,
  { visibility: BoardVisibility; body: WorkspaceBoardVisibilityPolicyPatch }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      visibility,
      body,
    }: {
      visibility: BoardVisibility;
      body: WorkspaceBoardVisibilityPolicyPatch;
    }) => updateWorkspaceBoardVisibilityPolicy(workspaceId, visibility, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.boardVisibilityPolicies(workspaceId),
        exact: true,
      });
    },
  });
}

export function useUpdateWorkspaceGuestInviteRule(
  workspaceId: string,
): UseMutationResult<
  WorkspaceGuestInviteRuleSetting,
  ApiError,
  WorkspaceGuestInviteRule
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (guestInviteRule: WorkspaceGuestInviteRule) =>
      updateWorkspaceGuestInviteRule(workspaceId, guestInviteRule),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.guestInviteRule(workspaceId),
        exact: true,
      });
    },
  });
}

export function useDeleteWorkspace(
  workspaceId: string,
): UseMutationResult<null, ApiError, { confirmName: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ confirmName }: { confirmName: string }) =>
      deleteWorkspace(workspaceId, confirmName),
    onSuccess: () => {
      removeWorkspaceFromCaches(queryClient, workspaceId);
    },
  });
}

export function useRemoveWorkspaceMember(
  workspaceId: string,
): UseMutationResult<null, ApiError, { userId: string; removeFromBoards: boolean }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      removeFromBoards,
    }: {
      userId: string;
      removeFromBoards: boolean;
    }) => removeWorkspaceMember(workspaceId, userId, removeFromBoards),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.members(workspaceId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.pages() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.boards(workspaceId),
      });
    },
  });
}

export function useRemoveWorkspaceGuest(
  workspaceId: string,
): UseMutationResult<null, ApiError, { userId: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      removeWorkspaceGuest(workspaceId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workspaces.guests(workspaceId),
        exact: true,
      });
    },
  });
}

export function useLeaveWorkspace(
  workspaceId: string,
): UseMutationResult<null, ApiError, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => leaveWorkspace(workspaceId),
    onSuccess: () => {
      removeWorkspaceFromCaches(queryClient, workspaceId);
    },
  });
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * The caller's own role in a workspace, or `null` when it cannot be known.
 *
 * ⚠ **There is no other source.** `boardGroupSchema` carries no viewer role and
 * `GET /workspaces` carries none either; the member list is the only route that
 * returns the caller's own row. `null` means *render read-only* — a control
 * that appears a moment late is better than one that appears and 403s
 * (`D2-BRIEF.md` §5.3).
 */
export function ownRole(
  members: readonly WorkspaceMember[] | undefined,
  userId: string | undefined,
): WorkspaceRole | null {
  if (members === undefined || userId === undefined) return null;
  return members.find((member) => member.id === userId)?.role ?? null;
}
